import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil, finalize, switchMap } from 'rxjs';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { EmptyResultComponent } from '../../../shared/components/empty-result/empty-result.component';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../../shared/components/pagination-bar/pagination-bar.component';
import {
  DateRangePickerComponent,
  DateRange,
  PeriodOption,
} from '../../../shared/components/date-range-picker/date-range-picker.component';
import { RewardsService } from '../../../services/rewards.service';
import {
  OfferedRewardRule,
  RewardsHistoryColumn,
  RewardsHistoryItemV2,
  RewardsHistoryPagination,
  OfferedRewardsResponse,
  RewardsHistoryResponse,
  RewardsHistoryFilters,
} from '../../models/rewards-v2.models';
import { getDateRangeForPeriod } from '../../utils/rewards-period.util';
import { OfferedRewardsCarouselComponent } from '../offered-rewards-carousel/offered-rewards-carousel.component';
import { OfferedRewardCardSkeletonComponent } from '../offered-reward-card-skeleton/offered-reward-card-skeleton.component';
import { RewardsHistoryTableV2Component } from '../rewards-history-table-v2/rewards-history-table-v2.component';
import {
  REWARDS_DEFAULT_PAGINATION,
  REWARDS_DEFAULT_PERIOD,
  REWARDS_ITEMS_PER_PAGE_OPTIONS,
  REWARDS_PERIOD_OPTIONS,
  REWARDS_TOAST_LIFETIME,
} from '../../utils/rewards-page.constants';

@Component({
  selector: 'app-rewards-v2',
  standalone: true,
  imports: [
    CommonModule,
    EmptyResultComponent,
    PaginationBarComponent,
    DateRangePickerComponent,
    OfferedRewardsCarouselComponent,
    OfferedRewardCardSkeletonComponent,
    RewardsHistoryTableV2Component,
  ],
  providers: [NotificationService],
  templateUrl: './rewards-v2.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./rewards-v2.component.scss'],
})
export class RewardsV2Component implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly historyLoad$ = new Subject<RewardsHistoryFilters>();

  isLoadingOffered = false;
  isLoadingHistory = false;

  lifetimeRewards = '$0.00';
  offeredRules: OfferedRewardRule[] = [];

  historyColumns: RewardsHistoryColumn[] = [];
  historyItems: RewardsHistoryItemV2[] = [];
  historyPagination: RewardsHistoryPagination = { total: 0, page: 1, limit: 10, totalPages: 0 };

  selectedPeriod: string = REWARDS_DEFAULT_PERIOD;
  isDateRangePickerOpen = false;
  customDateRange: DateRange = { startDate: null, endDate: null };
  readonly periodOptions: PeriodOption[] = REWARDS_PERIOD_OPTIONS;

  paginationData: PaginationData = { ...REWARDS_DEFAULT_PAGINATION };
  readonly itemsPerPageOptions: number[] = [...REWARDS_ITEMS_PER_PAGE_OPTIONS];
  readonly offeredCardSkeletons = [0, 1];

  constructor(
    private readonly rewardsService: RewardsService,
    private readonly messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.historyLoad$
      .pipe(
        switchMap(filters => {
          this.isLoadingHistory = true;
          return this.rewardsService.getRewardsHistoryV2(filters).pipe(
            finalize(() => {
              this.isLoadingHistory = false;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data: RewardsHistoryResponse) => {
          this.historyColumns = data.columns ?? [];
          this.historyItems = data.items ?? [];
          this.historyPagination = data.pagination ?? {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0,
          };
          this.updatePaginationData(this.historyPagination);
        },
        error: () => {
          this.historyColumns = [];
          this.historyItems = [];
          this.historyPagination = { total: 0, page: 1, limit: 10, totalPages: 0 };
          this.paginationData = {
            ...REWARDS_DEFAULT_PAGINATION,
            itemsPerPage: this.paginationData.itemsPerPage,
          };
          this.showError('Failed to load rewards history. Please try again.');
        },
      });

    this.loadOfferedRewards();
    this.loadRewardsHistory();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleDateRangePicker(): void {
    this.isDateRangePickerOpen = !this.isDateRangePickerOpen;
  }

  closeDateRangePicker(): void {
    this.isDateRangePickerOpen = false;
  }

  onDateRangePeriodChange(period: string): void {
    this.selectedPeriod = period;
  }

  onCustomDateRangeChange(dateRange: DateRange): void {
    this.customDateRange = dateRange;
  }

  onDateRangeApply(data: { period: string; customRange?: DateRange }): void {
    this.selectedPeriod = data.period;
    if (data.customRange) {
      this.customDateRange = data.customRange;
    }
    this.closeDateRangePicker();
    this.paginationData.currentPage = 1;
    this.loadRewardsHistory();
  }

  onPageChange(page: number): void {
    this.paginationData.currentPage = page;
    this.loadRewardsHistory();
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.paginationData.itemsPerPage = itemsPerPage;
    this.paginationData.currentPage = 1;
    this.loadRewardsHistory();
  }

  trackByRuleId(_: number, rule: OfferedRewardRule): string {
    return rule.id;
  }

  private loadOfferedRewards(): void {
    this.isLoadingOffered = true;
    this.rewardsService
      .getOfferedRewards()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingOffered = false))
      )
      .subscribe({
        next: (data: OfferedRewardsResponse) => {
          this.lifetimeRewards = `$${data.lifetime_earned.toFixed(2)}`;
          this.offeredRules = data.rules ?? [];
        },
        error: () => {
          this.lifetimeRewards = '$0.00';
          this.offeredRules = [];
          this.showError('Failed to load offered rewards. Please try again.');
        },
      });
  }

  private loadRewardsHistory(): void {
    this.historyLoad$.next(this.buildHistoryFilters());
  }

  private buildHistoryFilters(): RewardsHistoryFilters {
    const filters: RewardsHistoryFilters = {
      page: this.paginationData.currentPage,
      limit: this.paginationData.itemsPerPage,
    };

    const dateRange = getDateRangeForPeriod(this.selectedPeriod, this.customDateRange);
    if (dateRange) {
      filters.start_date = dateRange.start;
      filters.end_date = dateRange.end;
    }

    return filters;
  }

  private updatePaginationData(pagination: RewardsHistoryPagination): void {
    this.paginationData = {
      currentPage: pagination.page,
      totalPages: pagination.totalPages,
      totalItems: pagination.total,
      itemsPerPage: pagination.limit,
    };
  }

  private showError(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message,
      life: REWARDS_TOAST_LIFETIME,
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
}
